import React, { useContext, useEffect, useState, useCallback } from "react";
import api from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import { Box, Heading, Flex, Button, IconButton, useDisclosure, useBreakpointValue, useToast } from "@chakra-ui/react";
import { FaUpload } from "react-icons/fa";
import ErrorMessage from "../components/onlinemenu/ErrorMessage";
import LoadingSpinner from "../components/onlinemenu/LoadingSpinner";
import SettingsCard from "../components/pricelists/SettingsCard";
import PriceListTable from "../components/pricelists/PriceListTable";
import UploadModal from "../components/pricelists/UploadModal";
import DeleteModal from "../components/pricelists/DeleteModal";
import { translatePriceListError } from "../constants/priceLists";

const PriceListsPage = () => {
    const { currLocation } = useContext(AuthContext);
    const [settings, setSettings] = useState(null);
    const [priceLists, setPriceLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteData, setDeleteData] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const toast = useToast();

    const fetchPriceLists = useCallback(async () => {
        const response = await api.get(`price-lists/location/${currLocation}`);
        setPriceLists(response.data || []);
    }, [currLocation]);

    const fetchAll = useCallback(async () => {
        if (!currLocation) return;
        try {
            setLoading(true);
            const [settingsResponse] = await Promise.all([
                api.get(`location-settings/location/${currLocation}`),
                fetchPriceLists(),
            ]);
            setSettings(settingsResponse.data);
            setError(null);
        } catch (err) {
            setError("Neuspješno učitavanje cjenika.");
        } finally {
            setLoading(false);
        }
    }, [currLocation, fetchPriceLists]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const showError = (description) =>
        toast({ title: "Greška", description, status: "error", duration: 5000, isClosable: true });

    const handleSaveSettings = async (body) => {
        try {
            const response = await api.put(`location-settings/location/${currLocation}`, body);
            setSettings(response.data ?? { ...settings, ...body });
            toast({ title: "Postavke spremljene", status: "success", duration: 3000, isClosable: true });
        } catch (err) {
            showError(translatePriceListError(err, "Spremanje postavki nije uspjelo."));
        }
    };

    const openUploadModal = useCallback((priceList) => {
        setEditData(priceList || null);
        onUploadOpen();
    }, [onUploadOpen]);

    const openDeleteModal = useCallback((priceList) => {
        setDeleteData(priceList);
        onDeleteOpen();
    }, [onDeleteOpen]);

    // Throws with a user-facing message so the modal can show it inline.
    const handleUpload = async ({ file, validForDate }) => {
        const formData = new FormData();
        if (file) formData.append("File", file);
        if (validForDate) formData.append("ValidForDate", validForDate);

        try {
            if (editData) {
                // A published list comes back as a new version with a new id,
                // so always reload the list instead of patching it in place.
                await api.put(`price-lists/${editData.id}/location/${currLocation}`, formData);
            } else {
                await api.post(`price-lists/location/${currLocation}`, formData);
            }
        } catch (err) {
            throw new Error(translatePriceListError(err, "Spremanje cjenika nije uspjelo."));
        }

        setEditData(null);
        onUploadClose();
        try {
            await fetchPriceLists();
        } catch (err) {
            showError("Osvježavanje popisa cjenika nije uspjelo.");
        }
    };

    const handleDelete = async () => {
        if (!deleteData) return;
        try {
            await api.delete(`price-lists/${deleteData.id}/location/${currLocation}`);
            setPriceLists((prev) => prev.filter((priceList) => priceList.id !== deleteData.id));
            setDeleteData(null);
            onDeleteClose();
        } catch (err) {
            showError(translatePriceListError(err, "Brisanje cjenika nije uspjelo."));
        }
    };

    // The file endpoint needs the Bearer token, so a plain <a href> won't
    // work - fetch it through the api client and save it from a blob URL.
    const handleDownload = useCallback(async (priceList) => {
        setDownloadingId(priceList.id);
        try {
            const response = await api.get(`price-lists/${priceList.id}/location/${currLocation}/file`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = priceList.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast({ title: "Greška", description: "Preuzimanje cjenika nije uspjelo.", status: "error", duration: 5000, isClosable: true });
        } finally {
            setDownloadingId(null);
        }
    }, [currLocation, toast]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box
            as="main"
            pt={{ base: "24px", md: "20px" }}
            pl={{ base: "0px", md: "240px" }}
            pr={{ base: "0px", md: "30px" }}
            pb="20px"
            minH="100vh"
        >
            <Flex justify="space-between" align="center" mb={6} px={4} pt={4}>
                <Heading color="brand.700" size="lg">Sidreni cjenici</Heading>
                {isMobile ? (
                    <IconButton
                        icon={<FaUpload />}
                        aria-label="Učitaj cjenik"
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openUploadModal(null)}
                        _hover={{ bg: "brand.700" }}
                    />
                ) : (
                    <Button
                        leftIcon={<FaUpload />}
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openUploadModal(null)}
                        _hover={{ bg: "brand.700" }}
                    >
                        Učitaj cjenik
                    </Button>
                )}
            </Flex>

            <SettingsCard settings={settings} onSave={handleSaveSettings} />

            <Box
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.100"
                boxShadow="card"
                p={3}
                mx={4}
            >
                <PriceListTable
                    priceLists={priceLists}
                    onDownload={handleDownload}
                    onEdit={openUploadModal}
                    onDelete={openDeleteModal}
                    downloadingId={downloadingId}
                />
            </Box>

            <UploadModal
                isOpen={isUploadOpen}
                onClose={onUploadClose}
                onSave={handleUpload}
                priceList={editData}
            />

            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                onConfirm={handleDelete}
                data={deleteData}
            />
        </Box>
    );
};

export default PriceListsPage;
